/**
 * Days, times and lengths in the words a person would use, as the phone says
 * them (`DayWords.swift`). Days arrive as the workspace's own `YYYY-MM-DD`
 * strings and are read in UTC, so the browser's zone never decides what
 * counts as today.
 */

import { calendarZone } from '../../shared/calendar';

const utc = (day: string) => new Date(`${day}T00:00:00Z`);

/** Whole days from `from` to `to`; negative when `to` is earlier. */
export function dayDistance(from: string, to: string) {
  return Math.round((utc(to).getTime() - utc(from).getTime()) / 86_400_000);
}

export function weekday(day: string) {
  return utc(day).toLocaleDateString(undefined, { weekday: 'long', timeZone: 'UTC' });
}

/** "15 September", with the year only when it is not this one. */
export function calendarDate(day: string, today: string) {
  return utc(day).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    ...(day.slice(0, 4) === today.slice(0, 4) ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  });
}

/** "today", "tomorrow", "yesterday", a weekday within six days, otherwise a date. */
export function relativeDay(day: string, today: string) {
  const gap = dayDistance(today, day);

  if (gap === 0) {
    return 'today';
  }

  if (gap === 1) {
    return 'tomorrow';
  }

  if (gap === -1) {
    return 'yesterday';
  }

  return Math.abs(gap) < 7 ? weekday(day) : calendarDate(day, today);
}

/** "Monday" within the last six days, otherwise a date: for when something last happened. */
export function sinceDay(day: string, today: string) {
  return dayDistance(day, today) < 7 ? weekday(day) : calendarDate(day, today);
}

/** "Thursday, 24 September", from the workspace's own day. */
export function dayLine(day: string) {
  return utc(day).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
}

/** "17:52" or "5:52 PM", in the owner's own format and the workspace's zone. */
export function clockTime(date: Date | number | string) {
  return new Date(date).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: calendarZone(),
  });
}

/** "45m", "2h", "1h 30m". */
export function duration(minutes: number) {
  const hours = Math.floor(minutes / 60),
    rest = minutes % 60;

  if (!hours) {
    return `${rest}m`;
  }

  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

const numbers = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

/** "three", or "12" past nine. */
export function numberWord(value: number) {
  return value >= 0 && value < numbers.length ? numbers[value] : String(value);
}

export const capitalised = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** The hour in the workspace's zone. */
export function workspaceHour(date = new Date()) {
  return Number(
    new Intl.DateTimeFormat('en-GB', {
      hour: 'numeric',
      hourCycle: 'h23',
      timeZone: calendarZone(),
    }).format(date),
  );
}

export function greeting(hour: number) {
  if (hour >= 4 && hour < 12) {
    return 'Good morning';
  }

  return hour >= 12 && hour < 18 ? 'Good afternoon' : 'Good evening';
}
